/* eslint-disable @typescript-eslint/ban-ts-comment */
import { CSSResultGroup, TemplateResult, html, PropertyValues } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement, property, state, query } from 'lit/decorators.js';
import componentStyles from './idb-pie-chart.css.lit';
import * as Highcharts from 'highcharts';
import highchartsAccessibility from 'highcharts/modules/accessibility.js';
import {
  ChartData,
  CountryData,
  CountryDataChart,
  SingleChartData,
} from './types';

@customElement('idb-pie-chart')
export class IdbPieChart extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  @query('#pie-chart-container')
  container: HTMLDivElement;

  @property({ type: {}, attribute: 'data', reflect: true })
  data: string;

  @property({ type: String, attribute: 'single-percentage' })
  singlePercentage: string;

  @state()
  chartData: CountryDataChart;

  @state()
  singleData: SingleChartData;

  @state()
  totalByCountry: number;

  @state()
  projectsLabel: string;

  @state()
  countryName: string;

  options: Highcharts.Options;

  chart: Highcharts.Chart;

  connectedCallback = (): void => {
    super.connectedCallback();
    highchartsAccessibility(Highcharts);
  };

  protected firstUpdated = (): void => {
    if (this.singlePercentage) {
      this.handleSingleChartData();
      this.setSingleChart();
    } else {
      this.validateAttributes();
      this.handleCountryData();
      this.setInitialCountryInfo();
      this.setCountryChart();
    }
    this.chart = Highcharts.chart(this.options);
  };

  updated(_changedProperties: PropertyValues) {
    if (_changedProperties.has('data')) {
      this.handleCountryData();
      this.setInitialCountryInfo();
      this.chart.series[0].setData(this.chartData.data);
    }
  }

  validateAttributes = () => {
    if (!this.data) {
      throw new Error('Chart data must contain an object of data');
    }
  };

  setProjectLabel = (count: number) => {
    return count <= 1 ? 'Project' : 'Projects';
  };

  render = (): TemplateResult => {
    return html`
      <div class="pie-chart-wrapper">
        ${
          this.singlePercentage
            ? this.singleLayerTemplate()
            : this.countryLayerTemplate()
        }
        <div class="idb-pie-chart" id='pie-chart-container'></div>
      </div>
    `;
  };

  countryLayerTemplate = (): TemplateResult => {
    return html`
      <div class="slice-info">
        <div class="slice-info-count">${this.totalByCountry}</div>
        <div class="slice-info-label">${this.projectsLabel}</div>
        <div class="slice-info-name">${this.countryName}</div>
      </div>
    `;
  };

  singleLayerTemplate = (): TemplateResult => {
    return html`
      <div class="percentage-info">
        ${this.singlePercentage}
      </div>
    `;
  };

  setInitialCountryInfo = () => {
    this.setCountryInfo(parseInt(this.chartData.total_projects), '');
    this.projectsLabel = this.setProjectLabel(
      parseInt(this.chartData.total_projects)
    );
  };

  setCountryInfo = (totalByCountry: number, countryName: string): void => {
    this.totalByCountry = totalByCountry;
    this.countryName = countryName;
    this.projectsLabel = `${this.setProjectLabel(this.totalByCountry)} in`;
  };

  setProjectByCountryPercentage = (total: string, value: string): number => {
    const _total = parseInt(total) !== 0 ? parseInt(total) : 1;
    return Math.round((parseInt(value) / _total) * 100);
  };

  handleCountryData = (): void => {
    if (this.data) {
      const handleData: ChartData = JSON.parse(
        this.data as string
      ) as ChartData;
      this.chartData = {
        id: handleData.id,
        total_projects: handleData.total_projects,
        data: handleData.breakdown_a.map((item): CountryData => {
          return {
            name: `${this.setProjectByCountryPercentage(
              handleData.total_projects,
              item.TOTAL
            )}%`,
            countryName: item.name,
            y: parseInt(item.TOTAL),
            z: this.setProjectByCountryPercentage(
              handleData.total_projects,
              item.TOTAL
            ),
            selected: false,
            sliced: false,
          };
        }),
      };
    }
  };

  handleSingleChartData = (): void => {
    const percentage: number = parseFloat(this.singlePercentage.split('%')[0]);
    const remaining: number = 100 - percentage;
    this.singleData = {
      data: [
        {
          name: this.singlePercentage,
          y: percentage,
          selected: true,
          sliced: true,
        },
        {
          name: `${remaining}%`,
          y: remaining,
          selected: false,
          sliced: false,
        },
      ],
    };
  };

  setCountryChart = () => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const _this = this;
    this.options = {
      chart: {
        renderTo: this.container,
        type: 'pie',
        backgroundColor: 'transparent',
      },
      title: {
        text: '',
      },
      tooltip: {
        headerFormat: '',
        pointFormat: `<span style="color:{point.color}">\u25CF</span> <b> {point.countryName}</b><br/>
        <b>{point.y}</b> Projects<br/>`,
      },
      plotOptions: {
        pie: {
          innerSize: '60%',
          allowPointSelect: true,
          states: {
            select: {
              color: '#004E70',
            },
            hover: {
              color: '#004E70',
            },
          },
        },
      },
      colors: [
        '#1D9ADD',
        '#32A3DF',
        '#48ACE1',
        '#5DB5E4',
        '#73BEE6',
        '#93CBE9',
        '#A8D4EB',
      ],
      series: [
        {
          type: 'pie',
          allowPointSelect: true,
          data: this.chartData.data,
          point: {
            events: {
              mouseOver: function () {
                const options = this.options as CountryData;
                _this.setCountryInfo(options.y, options.countryName);
              },
            },
          },
        },
      ],
      credits: {
        enabled: false,
      },
    };
  };

  setSingleChart = () => {
    this.options = {
      chart: {
        renderTo: this.container,
        type: 'pie',
        backgroundColor: 'transparent',
        height: 300,
        width: 240,
        style: { margin: '0 auto' },
      },
      title: {
        text: '',
      },
      tooltip: {
        enabled: false,
      },
      plotOptions: {
        pie: {
          innerSize: '80%',
          allowPointSelect: false,
          borderWidth: 0,
          dataLabels: {
            enabled: false,
          },
        },
      },
      colors: ['#FFFFFF', '#1D9ADD'],
      series: [
        {
          type: 'pie',
          allowPointSelect: false,
          data: this.singleData.data,
        },
      ],
      credits: {
        enabled: false,
      },
    };
  };
}
